import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import ProgressMonitor from '../components/ProgressMonitor';
import ContentEditorModal from '../components/ContentEditorModal';
import ShareModal from '../components/ShareModal';
import PromptsEditor from '../components/PromptsEditor';
import { api } from '../services/api';

interface CFPage {
  id: number;
  job_id: number;
  title: string;
  slug: string;
  generator_type: string;
  meta_title: string | null;
  meta_description: string | null;
  sections: Record<string, any>;
  acf_fields: Record<string, any> | null;
  word_count: number | null;
  qa_score: number | null;
  qa_passed: boolean;
  qa_errors: string[] | null;
  qa_warnings: string[] | null;
  status: string;
  is_edited: boolean;
  created_at: string | null;
  edited_at: string | null;
  edited_by_name: string | null;
  generated_at: string | null;
}

interface CFResults {
  job: any;
  pages: CFPage[];
  total_pages: number;
  qa_summary?: { total_pages: number; qa_passed: number; qa_failed: number; average_score: number };
}

interface RecentJob {
  id: number;
  status: string;
  completed_at: string | null;
  created_at: string;
  settings: Record<string, any> | null;
}

interface BriefEntry {
  id: number;
  type: string;
  title: string;
  slug: string;
  brief: string;
}

interface FactSheet {
  job_id: number;
  label: string;
  completed_at: string | null;
  word_count: number | null;
  has_content: boolean;
  step_name: string | null;
}

const GENERATOR_TYPES = [
  { id: 'program', name: 'Program Pages', icon: '📋' },
  { id: 'condition', name: 'Condition Pages', icon: '🏥' },
  { id: 'sap', name: 'Service Area Pages', icon: '📍' },
];

let briefIdCounter = 1;

export default function ContentFrameworkCustomPage() {
  const { projectId: urlProjectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  // Support both new URL structure (/projects/:projectId/agent) and legacy (?project_id=...)
  const projectId = urlProjectId || searchParams.get('project_id');

  const [step, setStep] = useState<'briefs' | 'processing' | 'results'>('briefs');
  const [jobId, setJobId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Facts sheet state
  const [factSheets, setFactSheets] = useState<FactSheet[]>([]);
  const [selectedFactSheet, setSelectedFactSheet] = useState<number | null>(null);
  const [loadingFactSheets, setLoadingFactSheets] = useState(false);

  // Briefs state
  const [briefs, setBriefs] = useState<BriefEntry[]>([
    { id: briefIdCounter++, type: 'program', title: '', slug: '', brief: '' },
  ]);

  // Results state
  const [results, setResults] = useState<CFResults | null>(null);
  const [recentJobs, setRecentJobs] = useState<RecentJob[]>([]);

  // Prompts editor state
  const [showPrompts, setShowPrompts] = useState(false);

  // Editor modal state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingPage, setEditingPage] = useState<CFPage | null>(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [sharePageId, setSharePageId] = useState(0);
  const [sharePageTitle, setSharePageTitle] = useState('');

  // Bulk add state
  const [bulkAddOpen, setBulkAddOpen] = useState(false);
  const [bulkAddType, setBulkAddType] = useState('program');
  const [bulkAddText, setBulkAddText] = useState('');
  const [bulkAddBrief, setBulkAddBrief] = useState('');

  // Blank page creation state
  const [blankPageModal, setBlankPageModal] = useState<{ open: boolean; type: string }>({ open: false, type: '' });
  const [blankPageTitle, setBlankPageTitle] = useState('');
  const [blankPageSlug, setBlankPageSlug] = useState('');
  const [creatingBlank, setCreatingBlank] = useState(false);

  // ACF Editor modal state
  const [acfEditorOpen, setAcfEditorOpen] = useState(false);
  const [acfEditingPage, setAcfEditingPage] = useState<CFPage | null>(null);
  const [acfFields, setAcfFields] = useState<Record<string, any>>({});
  const [availableAcfFields, setAvailableAcfFields] = useState<string[]>([]);
  const [acfLoading, setAcfLoading] = useState(false);
  const [acfSaving, setAcfSaving] = useState(false);

  // CSV Conversion state
  const [convertingProgram, setConvertingProgram] = useState(false);
  const [convertingCondition, setConvertingCondition] = useState(false);
  const [convertingSap, setConvertingSap] = useState(false);
  const [conversionProgress, setConversionProgress] = useState<{
    type: string; progress: number; status: string; message: string;
    logs: string[]; total_pages?: number; converted_pages?: number;
  } | null>(null);

  const pollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (pollingTimeoutRef.current) clearTimeout(pollingTimeoutRef.current);
    };
  }, []);

  const urlJobId = searchParams.get('job_id');

  useEffect(() => {
    if (projectId) {
      loadRecentJobs();
      fetchFactSheets();
    }
  }, [projectId]);

  const fetchFactSheets = async () => {
    if (!projectId) return;
    try {
      setLoadingFactSheets(true);
      const response = await api.get(`/factsheets/project/${projectId}/selector`);
      setFactSheets(response.data.fact_sheets || []);
    } catch {
      setFactSheets([]);
    } finally {
      setLoadingFactSheets(false);
    }
  };

  useEffect(() => {
    if (urlJobId && projectId) {
      const id = parseInt(urlJobId);
      if (!isNaN(id)) viewJob({ id } as RecentJob);
    }
  }, [urlJobId, projectId]);

  const loadRecentJobs = async () => {
    if (!projectId) return;
    try {
      const response = await api.get(`/cpcustom/recent-jobs/${projectId}`);
      setRecentJobs(response.data || []);
    } catch { /* ignore */ }
  };

  const viewJob = async (job: RecentJob) => {
    try {
      setJobId(job.id);
      const response = await api.get(`/cpcustom/results/${job.id}`);
      setResults(response.data);
      setStep('results');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load job results');
    }
  };

  // ── Brief management ────────────────────────────────────────────────

  const addBrief = () => {
    setBriefs(prev => [...prev, { id: briefIdCounter++, type: 'program', title: '', slug: '', brief: '' }]);
  };

  const removeBrief = (id: number) => {
    setBriefs(prev => prev.length > 1 ? prev.filter(b => b.id !== id) : prev);
  };

  const updateBrief = (id: number, field: keyof BriefEntry, value: string) => {
    setBriefs(prev => prev.map(b => {
      if (b.id !== id) return b;
      const updated = { ...b, [field]: value };
      // Auto-generate slug from title
      if (field === 'title') {
        const autoSlug = value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
        if (!b.slug || b.slug === b.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')) {
          updated.slug = autoSlug;
        }
      }
      return updated;
    }));
  };

  const handleBulkAdd = () => {
    const lines = bulkAddText.split('\n').map(l => l.trim()).filter(Boolean);
    if (lines.length === 0) return;
    const newBriefs: BriefEntry[] = lines.map(title => ({
      id: briefIdCounter++,
      type: bulkAddType,
      title,
      slug: title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
      brief: bulkAddBrief.trim(),
    }));
    // Replace the initial empty brief if it's the only one and untouched
    setBriefs(prev => {
      if (prev.length === 1 && !prev[0].title && !prev[0].brief) return newBriefs;
      return [...prev, ...newBriefs];
    });
    setBulkAddOpen(false);
    setBulkAddText('');
    setBulkAddBrief('');
  };

  // ── Generate ────────────────────────────────────────────────────────

  const handleGenerate = async () => {
    if (!projectId) return;

    const validBriefs = briefs.filter(b => b.title.trim() && b.brief.trim());
    if (validBriefs.length === 0) {
      setError('Please add at least one brief with a title and brief text.');
      return;
    }

    setError(null);
    setIsSubmitting(true);
    try {
      // Create job with briefs
      const res = await api.post('/cpcustom/create-job', {
        project_id: parseInt(projectId),
        briefs: validBriefs.map(b => ({
          type: b.type,
          title: b.title.trim(),
          slug: b.slug.trim(),
          brief: b.brief.trim(),
        })),
        factsagent_job_id: selectedFactSheet || undefined,
      });
      const newJobId = res.data.job_id;
      setJobId(newJobId);

      // Start generation
      await api.post(`/cpcustom/generate/${newJobId}`);
      setStep('processing');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to start generation');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Results helpers ─────────────────────────────────────────────────

  const handleReset = () => {
    setStep('briefs');
    setJobId(null);
    setError(null);
    setResults(null);
    setBriefs([{ id: briefIdCounter++, type: 'program', title: '', slug: '', brief: '' }]);
    loadRecentJobs();
  };

  const handleDelete = async () => {
    if (!jobId) return;
    if (!confirm('Are you sure you want to delete this job?')) return;
    try {
      await api.delete(`/cpcustom/${jobId}`);
      handleReset();
    } catch (err: any) {
      alert('Failed to delete job: ' + (err.response?.data?.detail || err.message));
    }
  };

  const openEditor = (page: CFPage) => { setEditingPage(page); setEditorOpen(true); };
  const closeEditor = () => { setEditorOpen(false); setEditingPage(null); };

  const handleEditorSave = async () => {
    if (jobId) {
      try {
        const response = await api.get(`/cpcustom/results/${jobId}`);
        setResults(response.data);
      } catch { /* ignore */ }
    }
  };

  const openBlankPageModal = (type: string) => {
    setBlankPageModal({ open: true, type });
    setBlankPageTitle('');
    setBlankPageSlug('');
  };

  const createBlankPage = async () => {
    if (!jobId || !blankPageTitle.trim()) return;
    setCreatingBlank(true);
    try {
      const formData = new FormData();
      formData.append('title', blankPageTitle.trim());
      formData.append('slug', blankPageSlug.trim());
      formData.append('generator_type', blankPageModal.type);
      await api.post(`/cppages/${jobId}/pages/create-blank`, formData);
      setBlankPageModal({ open: false, type: '' });
      const response = await api.get(`/cpcustom/results/${jobId}`);
      setResults(response.data);
    } catch (err: any) {
      alert('Failed to create page: ' + (err.response?.data?.detail || err.message));
    } finally {
      setCreatingBlank(false);
    }
  };

  const handlePageRewrite = async (pageId: number) => {
    try {
      await api.post(`/cppages/pages/${pageId}/regenerate`);
      closeEditor();
      if (jobId) {
        const pollInterval = setInterval(async () => {
          try {
            const response = await api.get(`/cpcustom/results/${jobId}`);
            setResults(response.data);
            const job = await api.get(`/cpcustom/status/${jobId}`);
            if (job.data.status !== 'processing') clearInterval(pollInterval);
          } catch { clearInterval(pollInterval); }
        }, 5000);
        setTimeout(() => clearInterval(pollInterval), 300000);
      }
    } catch (err: any) {
      alert('Rewrite failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  // ── ACF Editor ──────────────────────────────────────────────────────

  const openAcfEditor = async (page: CFPage) => {
    setAcfEditingPage(page); setAcfLoading(true); setAcfEditorOpen(true);
    try {
      const response = await api.get(`/cppages/pages/${page.id}/acf-fields`);
      setAcfFields(response.data.acf_fields || {});
      setAvailableAcfFields(response.data.available_fields || []);
    } catch { setError('Failed to load ACF fields'); }
    finally { setAcfLoading(false); }
  };

  const closeAcfEditor = () => { setAcfEditorOpen(false); setAcfEditingPage(null); setAcfFields({}); setAvailableAcfFields([]); };

  const handleAcfSave = async () => {
    if (!acfEditingPage) return;
    setAcfSaving(true);
    try {
      await api.put(`/cppages/pages/${acfEditingPage.id}/acf-fields`, { acf_fields: acfFields, edited_by_name: 'User' });
      if (jobId) { const r = await api.get(`/cpcustom/results/${jobId}`); setResults(r.data); }
      closeAcfEditor();
    } catch (err: any) { setError(err.response?.data?.detail || 'Failed to save ACF fields'); }
    finally { setAcfSaving(false); }
  };

  // ── CSV conversion ──────────────────────────────────────────────────

  const convertToAcf = async (type: 'program' | 'condition' | 'sap') => {
    if (!jobId) return;
    const setConverting = type === 'program' ? setConvertingProgram : type === 'condition' ? setConvertingCondition : setConvertingSap;
    setConverting(true); setConversionProgress(null); setError(null);
    if (pollingTimeoutRef.current) { clearTimeout(pollingTimeoutRef.current); pollingTimeoutRef.current = null; }

    try {
      const response = await api.post(`/cppages/${jobId}/convert-all-to-acf?generator_type=${type}`);
      const { conversion_job_id } = response.data;
      if (!isMountedRef.current) return;

      const pollStatus = async () => {
        if (!isMountedRef.current) return;
        try {
          const statusResponse = await api.get(`/cppages/acf-conversion/${conversion_job_id}/status`);
          const status = statusResponse.data;
          if (!isMountedRef.current) return;
          setConversionProgress({
            type, progress: status.progress, status: status.status,
            message: status.recent_logs?.[status.recent_logs.length - 1] || 'Processing...',
            logs: status.recent_logs || [], total_pages: status.total_pages, converted_pages: status.converted_pages,
          });
          if (status.status === 'completed' || status.status === 'failed') {
            setConverting(false); pollingTimeoutRef.current = null;
            if (isMountedRef.current) { const r = await api.get(`/cpcustom/results/${jobId}`); if (isMountedRef.current) setResults(r.data); }
            if (status.status === 'failed' && isMountedRef.current) setError(status.error_message || `Failed to convert ${type} pages`);
          } else if (isMountedRef.current) {
            pollingTimeoutRef.current = setTimeout(pollStatus, 2000);
          }
        } catch { if (isMountedRef.current) { setConverting(false); setError('Failed to get conversion status'); } pollingTimeoutRef.current = null; }
      };
      pollingTimeoutRef.current = setTimeout(pollStatus, 1000);
    } catch (err: any) {
      if (isMountedRef.current) { setError(err.response?.data?.detail || `Failed to start ${type} ACF conversion`); setConverting(false); }
    }
  };

  // ── Computed values ─────────────────────────────────────────────────

  const programPagesWithAcf = results?.pages.filter(p => ['program', 'program_csv'].includes(p.generator_type) && p.acf_fields).length || 0;
  const conditionPagesWithAcf = results?.pages.filter(p => p.generator_type === 'condition' && p.acf_fields).length || 0;
  const totalProgramPages = results?.pages.filter(p => ['program', 'program_csv'].includes(p.generator_type)).length || 0;
  const totalConditionPages = results?.pages.filter(p => p.generator_type === 'condition').length || 0;
  const sapPagesWithAcf = results?.pages.filter(p => p.generator_type === 'sap' && p.acf_fields).length || 0;
  const totalSapPages = results?.pages.filter(p => p.generator_type === 'sap').length || 0;
  const hasProgramPages = results?.pages.some(p => ['program', 'program_csv'].includes(p.generator_type)) || false;
  const hasConditionPages = results?.pages.some(p => p.generator_type === 'condition') || false;
  const hasSapPages = results?.pages.some(p => p.generator_type === 'sap') || false;

  const pagesByType = results?.pages.reduce((acc, page) => {
    const type = page.generator_type;
    if (!acc[type]) acc[type] = [];
    acc[type].push(page);
    return acc;
  }, {} as Record<string, CFPage[]>) || {};

  if (!projectId) {
    return (
      <Layout>
        <div className="text-center py-20 text-gray-500">
          <p className="text-lg font-medium">No Project Selected</p>
          <p className="mt-2">Select a project from the Projects page.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout projectId={parseInt(projectId)}>
      <div className="animate-fade-in">
        {/* Page Header */}
        <div className="page-header">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="page-title">Content Framework Custom</h1>
              <p className="page-subtitle">
                Provide your own brief for each page &mdash; AI generates content based on your instructions
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowPrompts(!showPrompts)}
                className={showPrompts ? 'btn-primary' : 'btn-secondary'}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                {showPrompts ? 'Hide Prompts' : 'Edit Prompts'}
              </button>
              {step === 'results' && (
                <>
                  <a href={`/api/cpcustom/${jobId}/download-all?format=md&token=${localStorage.getItem('auth_token') || ''}`} download className="btn-primary">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Download Results
                  </a>
                  <button onClick={handleReset} className="btn-secondary">New Job</button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 bg-danger-50 border border-danger-200 rounded-lg flex items-start gap-3">
            <svg className="w-5 h-5 text-danger-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-danger-700">{error}</span>
            <button onClick={() => setError(null)} className="ml-auto text-danger-400 hover:text-danger-600">&times;</button>
          </div>
        )}

        {/* Prompts Editor — only pipeline + skills (no briefs or structure rules) */}
        {showPrompts && (
          <div className="mb-8">
            <PromptsEditor
              systemType="contentframeworkv2"
              title="Content Framework Prompts (Shared with CF V2)"
              subtitle="These skills and prompts are shared with Content Framework V2. Changes here apply to both systems."
              categories={{
                order: ['skills'],
                names: { skills: 'Skills & Guidelines' },
                icons: { skills: '⚡' },
              }}
              onClose={() => setShowPrompts(false)}
            />
          </div>
        )}

        {/* ═══════════ BRIEFS STEP ═══════════ */}
        {step === 'briefs' && (
          <>
            {/* Recent Jobs */}
            {recentJobs.length > 0 && (
              <div className="mb-8">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Custom Jobs</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {recentJobs.map((job) => (
                    <div
                      key={job.id}
                      onClick={() => viewJob(job)}
                      className={`card hover:shadow-lg transition-all cursor-pointer group ${
                        job.status === 'failed' ? 'hover:border-amber-300 border-amber-200' : 'hover:border-brand-200'
                      }`}
                    >
                      <div className="card-body">
                        <div className="flex items-start space-x-3">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                            job.status === 'failed'
                              ? 'bg-gradient-to-br from-amber-400 to-amber-600'
                              : 'bg-gradient-to-br from-success-400 to-success-600'
                          }`}>
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={job.status === 'failed'
                                ? "M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                                : "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"} />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-base font-bold text-gray-900 group-hover:text-brand-600 transition-colors">
                                {(job.settings?.briefs as any[])?.length || 0} page(s)
                              </span>
                              <span className="text-xs text-gray-400">#{job.id}</span>
                            </div>
                            <p className="text-sm text-gray-500 mt-1">
                              {new Date(job.completed_at || job.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <span className="text-sm font-medium text-brand-600 group-hover:translate-x-1 transition-transform inline-flex items-center gap-1">
                            View/Edit Pages
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Facts Sheet Selector */}
            <div className="max-w-4xl mx-auto mb-6">
              <div className="card">
                <div className="card-body">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-400 to-indigo-600 flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h2 className="text-lg font-semibold text-gray-900">Facility Facts</h2>
                      <p className="text-sm text-gray-500">Optional — select a FactsAgent output to include facility data in generations.</p>
                    </div>
                    {selectedFactSheet && (
                      <button
                        onClick={() => setSelectedFactSheet(null)}
                        className="text-sm text-gray-400 hover:text-red-500"
                      >
                        Clear
                      </button>
                    )}
                  </div>

                  {loadingFactSheets ? (
                    <div className="flex items-center justify-center py-4">
                      <span className="spinner mr-2"></span>
                      <span className="text-gray-500 text-sm">Loading fact sheets...</span>
                    </div>
                  ) : factSheets.length === 0 ? (
                    <p className="text-sm text-gray-400 py-2">No FactsAgent outputs found for this project. You can still generate pages without facts.</p>
                  ) : (
                    <div className="space-y-2">
                      {factSheets.map((sheet) => (
                        <div
                          key={sheet.job_id}
                          onClick={() => setSelectedFactSheet(sheet.job_id)}
                          className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                            selectedFactSheet === sheet.job_id
                              ? 'border-indigo-500 bg-indigo-50'
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                                selectedFactSheet === sheet.job_id
                                  ? 'border-indigo-500 bg-indigo-500'
                                  : 'border-gray-300'
                              }`}>
                                {selectedFactSheet === sheet.job_id && (
                                  <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                )}
                              </div>
                              <div>
                                <p className="font-medium text-gray-900 text-sm">{sheet.label}</p>
                                <p className="text-xs text-gray-500">
                                  Job #{sheet.job_id}
                                  {sheet.word_count && ` - ${sheet.word_count.toLocaleString()} words`}
                                </p>
                              </div>
                            </div>
                            {sheet.completed_at && (
                              <span className="text-xs text-gray-400">
                                {new Date(sheet.completed_at).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Briefs Input */}
            <div className="max-w-4xl mx-auto">
              <div className="card">
                <div className="card-body">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900">Page Briefs</h2>
                      <p className="text-gray-500">Add a brief for each page you want to generate. The AI will follow your instructions exactly.</p>
                    </div>
                  </div>

                  <div className="space-y-6">
                    {briefs.map((brief, idx) => (
                      <div key={brief.id} className="border border-gray-200 rounded-xl p-5 bg-gray-50/50">
                        <div className="flex items-center justify-between mb-4">
                          <span className="text-sm font-semibold text-gray-700">Page {idx + 1}</span>
                          {briefs.length > 1 && (
                            <button
                              onClick={() => removeBrief(brief.id)}
                              className="text-red-400 hover:text-red-600 text-sm font-medium"
                            >
                              Remove
                            </button>
                          )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Page Type</label>
                            <select
                              value={brief.type}
                              onChange={e => updateBrief(brief.id, 'type', e.target.value)}
                              className="input"
                            >
                              {GENERATOR_TYPES.map(t => (
                                <option key={t.id} value={t.id}>{t.icon} {t.name}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Page Title *</label>
                            <input
                              type="text"
                              value={brief.title}
                              onChange={e => updateBrief(brief.id, 'title', e.target.value)}
                              placeholder="e.g. Alcohol Addiction Treatment"
                              className="input"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">URL Slug</label>
                            <input
                              type="text"
                              value={brief.slug}
                              onChange={e => updateBrief(brief.id, 'slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]+/g, '-'))}
                              placeholder="auto-generated"
                              className="input font-mono text-sm"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Page Brief * <span className="text-gray-400 font-normal">&mdash; your instructions for the AI</span>
                          </label>
                          <textarea
                            value={brief.brief}
                            onChange={e => updateBrief(brief.id, 'brief', e.target.value)}
                            placeholder={"Write detailed instructions for this page. Include:\n- What topics to cover\n- Key points to emphasize\n- Target audience\n- Specific sections you want\n- Any statistics or data to include\n- Tone and style preferences"}
                            rows={6}
                            className="textarea font-mono text-sm"
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-4 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={addBrief}
                        className="btn-secondary"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add Brief
                      </button>
                      <button
                        onClick={() => setBulkAddOpen(true)}
                        className="btn-secondary"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                        </svg>
                        Bulk Add
                      </button>
                    </div>

                    <button
                      onClick={handleGenerate}
                      disabled={isSubmitting || briefs.every(b => !b.title.trim() || !b.brief.trim())}
                      className="btn-primary"
                    >
                      {isSubmitting ? (
                        <><span className="spinner"></span> Starting...</>
                      ) : (
                        <>
                          Generate {briefs.filter(b => b.title.trim() && b.brief.trim()).length} Page(s)
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Bulk Add Modal */}
            {bulkAddOpen && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setBulkAddOpen(false)}>
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 p-6" onClick={e => e.stopPropagation()}>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Bulk Add Pages</h3>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Page Type</label>
                    <select value={bulkAddType} onChange={e => setBulkAddType(e.target.value)} className="input">
                      {GENERATOR_TYPES.map(t => (
                        <option key={t.id} value={t.id}>{t.icon} {t.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Page Titles <span className="text-gray-400 font-normal">(one per line)</span>
                    </label>
                    <textarea
                      value={bulkAddText}
                      onChange={e => setBulkAddText(e.target.value)}
                      placeholder={"Alcohol Addiction Treatment\nDrug Rehab Program\nOpioid Recovery Program\nMental Health Treatment"}
                      rows={6}
                      className="textarea font-mono text-sm"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      {bulkAddText.split('\n').filter(l => l.trim()).length} page(s) will be added. Slugs auto-generated from titles.
                    </p>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Shared Brief <span className="text-gray-400 font-normal">(applied to all pages above)</span>
                    </label>
                    <textarea
                      value={bulkAddBrief}
                      onChange={e => setBulkAddBrief(e.target.value)}
                      placeholder="Write a brief that will be used for all pages listed above. You can edit individual briefs after adding."
                      rows={4}
                      className="textarea font-mono text-sm"
                    />
                  </div>

                  <div className="flex justify-end gap-3">
                    <button onClick={() => setBulkAddOpen(false)} className="btn-secondary">Cancel</button>
                    <button
                      onClick={handleBulkAdd}
                      disabled={!bulkAddText.trim()}
                      className="btn-primary"
                    >
                      Add {bulkAddText.split('\n').filter(l => l.trim()).length} Page(s)
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* ═══════════ PROCESSING STEP ═══════════ */}
        {step === 'processing' && jobId && (
          <div className="max-w-4xl mx-auto">
            <div className="card">
              <div className="card-body">
                <div className="mb-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">Generating Pages from Briefs</h2>
                  <p className="text-gray-500">Processing {briefs.filter(b => b.title.trim() && b.brief.trim()).length} page(s). Each page is generated individually via Claude.</p>
                </div>
                <ProgressMonitor
                  jobId={jobId}
                  systemType="cpcustom"
                  onStatusUpdate={async (status) => {
                    if (status.status === 'completed' || status.status === 'failed') {
                      try {
                        const response = await api.get(`/cpcustom/results/${jobId}`);
                        setResults(response.data);
                        if (status.status === 'failed') setError(status.error_message || 'Generation partially failed');
                        setStep('results');
                      } catch {
                        setStep('results');
                      }
                    }
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* ═══════════ RESULTS STEP ═══════════ */}
        {step === 'results' && jobId && results && (
          <div className="max-w-6xl mx-auto">
            {/* Results Header */}
            <div className="card mb-6">
              <div className="card-header bg-success-50">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-success-100 flex items-center justify-center">
                    <svg className="w-6 h-6 text-success-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-success-900">Generation Complete</h2>
                    <p className="text-sm text-success-700">
                      {results.total_pages} pages generated from custom briefs
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Pages Grid by Type */}
            {Object.entries(pagesByType).map(([type, pages]) => {
              const typeInfo = GENERATOR_TYPES.find(t => t.id === type);
              return (
                <div key={type} className="mb-8">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-2xl">{typeInfo?.icon || '📄'}</span>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {typeInfo?.name || type} ({pages.length} pages)
                    </h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {pages.map((page) => (
                      <div
                        key={page.id}
                        className="card hover:shadow-lg hover:border-brand-200 transition-all cursor-pointer group"
                        onClick={() => openEditor(page)}
                      >
                        <div className="card-body">
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <h4 className="font-semibold text-gray-900 group-hover:text-brand-600 transition-colors truncate">{page.title}</h4>
                              {page.slug && <p className="text-xs text-gray-400 mt-1 truncate">/{page.slug}</p>}
                              {page.meta_description && <p className="text-xs text-gray-500 mt-2 line-clamp-2">{page.meta_description}</p>}
                              <div className="flex items-center gap-2 mt-2 text-xs text-gray-500 flex-wrap">
                                {page.word_count && <span>{page.word_count.toLocaleString()} words</span>}
                                {page.is_edited && <span className="inline-flex items-center px-2 py-0.5 rounded font-medium bg-blue-100 text-blue-800">Edited</span>}
                              </div>
                            </div>
                            <a href={`/api/cppages/pages/${page.id}/download?format=md&token=${localStorage.getItem('auth_token') || ''}`} download onClick={e => e.stopPropagation()} className="btn-ghost btn-sm" title="Download">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                              </svg>
                            </a>
                          </div>
                          <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
                            <span className="text-brand-600 text-sm font-medium inline-flex items-center gap-1">
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                              Edit Content
                            </span>
                            <div className="flex items-center gap-2">
                              <button onClick={e => { e.stopPropagation(); openAcfEditor(page); }} className="btn-secondary btn-sm" title="Edit ACF Fields">
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                                </svg>
                                ACF
                              </button>
                              <button onClick={e => { e.stopPropagation(); openEditor(page); }} className="btn-primary btn-sm">Edit</button>
                              <button onClick={e => { e.stopPropagation(); setSharePageId(page.id); setSharePageTitle(page.title || 'Untitled'); setShareModalOpen(true); }} className="px-2.5 py-1 text-xs font-medium text-emerald-600 bg-emerald-50 hover:bg-emerald-100 rounded-lg">Share</button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* + Create Blank Page Card */}
                    <div
                      className="card border-2 border-dashed border-gray-300 hover:border-brand-400 hover:bg-brand-50 transition-all cursor-pointer group flex items-center justify-center min-h-[140px]"
                      onClick={() => openBlankPageModal(type)}
                    >
                      <div className="text-center py-6">
                        <svg className="w-8 h-8 mx-auto text-gray-400 group-hover:text-brand-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        <p className="mt-2 text-sm font-medium text-gray-500 group-hover:text-brand-600">Create Blank Page</p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* CSV Export Section */}
            <div className="card mb-6">
              <div className="card-header">
                <h3 className="font-semibold text-gray-900">Export to WP All Import CSV</h3>
                <p className="text-sm text-gray-500 mt-1">Convert content to ACF fields, then download CSV files formatted for WordPress All Import</p>
              </div>
              <div className="card-body space-y-4">
                {conversionProgress && (convertingProgram || convertingCondition || convertingSap) && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-blue-800">Converting {conversionProgress.type} pages to ACF fields...</span>
                      <span className="text-sm text-blue-600">{conversionProgress.progress}%</span>
                    </div>
                    <div className="w-full bg-blue-200 rounded-full h-2 mb-3">
                      <div className="bg-blue-600 h-2 rounded-full transition-all duration-300" style={{ width: `${conversionProgress.progress}%` }}></div>
                    </div>
                    <p className="text-xs text-blue-700 mb-2">{conversionProgress.message}</p>
                    {conversionProgress.logs.length > 0 && (
                      <div className="max-h-32 overflow-y-auto bg-white rounded p-2 text-xs font-mono text-gray-600">
                        {conversionProgress.logs.slice(-5).map((log, idx) => <div key={idx} className="py-0.5">{log}</div>)}
                      </div>
                    )}
                  </div>
                )}

                {conversionProgress && !convertingProgram && !convertingCondition && !convertingSap && conversionProgress.status === 'completed' && (
                  <div className="p-3 rounded-lg bg-green-50 border border-green-200">
                    <p className="text-sm text-green-800">
                      <strong>{conversionProgress.type.charAt(0).toUpperCase() + conversionProgress.type.slice(1)} pages:</strong>{' '}
                      {conversionProgress.converted_pages} of {conversionProgress.total_pages} converted successfully
                    </p>
                  </div>
                )}

                {hasProgramPages && (
                  <div className="p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h4 className="font-medium text-gray-900">Program Pages</h4>
                        <p className="text-sm text-gray-500">{programPagesWithAcf} of {totalProgramPages} converted</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => convertToAcf('program')} disabled={convertingProgram} className="btn-secondary">
                          {convertingProgram ? <><span className="spinner"></span> Converting...</> : <>Convert to ACF</>}
                        </button>
                        <a href={`/api/cppages/${jobId}/download-program-csv?token=${localStorage.getItem('auth_token') || ''}`} download
                          className={programPagesWithAcf > 0 ? 'btn-primary ring-2 ring-green-400 ring-offset-2 animate-pulse hover:animate-none' : 'btn-secondary opacity-50 cursor-not-allowed pointer-events-none'}
                          onClick={e => programPagesWithAcf === 0 && e.preventDefault()}>Download CSV</a>
                      </div>
                    </div>
                    {programPagesWithAcf === 0 && <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">Click "Convert to ACF" first</p>}
                  </div>
                )}

                {hasConditionPages && (
                  <div className="p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h4 className="font-medium text-gray-900">Condition Pages</h4>
                        <p className="text-sm text-gray-500">{conditionPagesWithAcf} of {totalConditionPages} converted</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => convertToAcf('condition')} disabled={convertingCondition} className="btn-secondary">
                          {convertingCondition ? <><span className="spinner"></span> Converting...</> : <>Convert to ACF</>}
                        </button>
                        <a href={`/api/cppages/${jobId}/download-condition-csv?token=${localStorage.getItem('auth_token') || ''}`} download
                          className={conditionPagesWithAcf > 0 ? 'btn-accent ring-2 ring-green-400 ring-offset-2 animate-pulse hover:animate-none' : 'btn-secondary opacity-50 cursor-not-allowed pointer-events-none'}
                          onClick={e => conditionPagesWithAcf === 0 && e.preventDefault()}>Download CSV</a>
                      </div>
                    </div>
                    {conditionPagesWithAcf === 0 && <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">Click "Convert to ACF" first</p>}
                  </div>
                )}

                {hasSapPages && (
                  <div className="p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h4 className="font-medium text-gray-900">Service Area Pages (SAP)</h4>
                        <p className="text-sm text-gray-500">{sapPagesWithAcf} of {totalSapPages} converted</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => convertToAcf('sap')} disabled={convertingSap} className="btn-secondary">
                          {convertingSap ? <><span className="spinner"></span> Converting...</> : <>Convert to ACF</>}
                        </button>
                        <a href={`/api/cppages/${jobId}/download-sap-csv?token=${localStorage.getItem('auth_token') || ''}`} download
                          className={sapPagesWithAcf > 0 ? 'btn-primary ring-2 ring-green-400 ring-offset-2 animate-pulse hover:animate-none' : 'btn-secondary opacity-50 cursor-not-allowed pointer-events-none'}
                          onClick={e => sapPagesWithAcf === 0 && e.preventDefault()}>Download CSV</a>
                      </div>
                    </div>
                    {sapPagesWithAcf === 0 && <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">Click "Convert to ACF" first</p>}
                  </div>
                )}

                {!hasProgramPages && !hasConditionPages && !hasSapPages && (
                  <p className="text-gray-500 text-sm">No pages available for CSV export</p>
                )}
              </div>
            </div>

            {/* Job Management */}
            <div className="card">
              <div className="card-body">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900">Custom Pages</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      #{results.job.id} | {results.total_pages} pages generated
                      {results.job.completed_at && ` | Completed: ${new Date(results.job.completed_at).toLocaleString()}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <a href={`/api/cpcustom/${jobId}/download-all?format=md&token=${localStorage.getItem('auth_token') || ''}`} download className="btn-primary">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Download All (ZIP)
                    </a>
                    <button onClick={handleReset} className="btn-secondary">New Job</button>
                    <button onClick={handleDelete} className="btn-danger">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Delete Job
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Content Editor Modal */}
      {editorOpen && editingPage && (
        <ContentEditorModal
          contentId={editingPage.id}
          contentTitle={editingPage.title}
          contentType={editingPage.generator_type}
          apiEndpoint={`/cpcustom/pages/${editingPage.id}/content`}
          isOpen={editorOpen}
          onClose={closeEditor}
          onSave={handleEditorSave}
          onRewrite={() => handlePageRewrite(editingPage.id)}
        />
      )}

      {/* ACF Fields Editor Modal */}
      {acfEditorOpen && acfEditingPage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Edit ACF Fields</h2>
                <p className="text-sm text-gray-500">{acfEditingPage.title}</p>
              </div>
              <button onClick={closeAcfEditor} className="p-2 hover:bg-gray-100 rounded-lg">
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {acfLoading ? (
                <div className="flex items-center justify-center py-12">
                  <span className="spinner mr-2"></span> <span className="text-gray-500">Loading ACF fields...</span>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-4">
                    <p className="text-sm text-blue-800"><strong>{availableAcfFields.length}</strong> ACF fields available for {acfEditingPage.generator_type} pages.</p>
                  </div>
                  <div className="grid grid-cols-1 gap-4">
                    {availableAcfFields.map((fieldName) => (
                      <div key={fieldName} className="border border-gray-200 rounded-lg p-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">{fieldName}</label>
                        {(fieldName.includes('_content') || fieldName.includes('_intro') || fieldName.includes('_readmore') || fieldName.includes('_info') || fieldName === 'seo_desc' || fieldName.includes('_a')) ? (
                          <textarea value={acfFields[fieldName] || ''} onChange={e => setAcfFields(prev => ({ ...prev, [fieldName]: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm font-mono" rows={4} placeholder={`Enter ${fieldName}...`} />
                        ) : (
                          <input type="text" value={acfFields[fieldName] || ''} onChange={e => setAcfFields(prev => ({ ...prev, [fieldName]: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm" placeholder={`Enter ${fieldName}...`} />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <button onClick={closeAcfEditor} className="btn-secondary" disabled={acfSaving}>Cancel</button>
              <button onClick={handleAcfSave} disabled={acfLoading || acfSaving} className="btn-primary">
                {acfSaving ? <><span className="spinner"></span> Saving...</> : <>Save ACF Fields</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Blank Page Modal */}
      {blankPageModal.open && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setBlankPageModal({ open: false, type: '' })} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  Create Blank {blankPageModal.type === 'sap' ? 'SAP' : blankPageModal.type === 'program' ? 'Program' : 'Condition'} Page
                </h3>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="label">Page Title *</label>
                  <input type="text" value={blankPageTitle} onChange={e => {
                    setBlankPageTitle(e.target.value);
                    if (!blankPageSlug || blankPageSlug === blankPageTitle.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''))
                      setBlankPageSlug(e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''));
                  }} placeholder="e.g. Alcohol Addiction Treatment" className="input" autoFocus />
                </div>
                <div>
                  <label className="label">URL Slug</label>
                  <input type="text" value={blankPageSlug} onChange={e => setBlankPageSlug(e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''))} className="input font-mono text-sm" />
                </div>
              </div>
              <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                <button onClick={() => setBlankPageModal({ open: false, type: '' })} className="btn-secondary">Cancel</button>
                <button onClick={createBlankPage} disabled={creatingBlank || !blankPageTitle.trim()} className="btn-primary">
                  {creatingBlank ? <><span className="spinner"></span> Creating...</> : <>Create Page</>}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
      <ShareModal
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        contentType="cpcustom"
        contentId={sharePageId}
        contentTitle={sharePageTitle}
      />
    </Layout>
  );
}
